import * as React from "react";
import { ScrollText } from "lucide-react";

const SUGGESTED_SKILL_PATHS = {
  excel: ["/dcf", "/lbo", "/trading-comps"],
  powerpoint: ["/company-profile-slide", "/industry-primer", "/strip-profiles"],
} as const;

export interface SuggestedSkillsSelectionState {
  availableSkillSlugs: readonly string[];
  selectedSkillSlugs: readonly string[];
  selectionAtLimit: boolean;
}

export interface GettingStartedSkillsProps {
  host: keyof typeof SUGGESTED_SKILL_PATHS;
  manageSkillsUrl?: string;
  availableSkillSlugs?: SuggestedSkillsSelectionState["availableSkillSlugs"];
  selectedSkillSlugs?: SuggestedSkillsSelectionState["selectedSkillSlugs"];
  selectionAtLimit?: SuggestedSkillsSelectionState["selectionAtLimit"];
  onSelectSuggestedSkill?: (path: string) => void;
}

export const GettingStartedSkills: React.FC<GettingStartedSkillsProps> = ({
  host,
  manageSkillsUrl,
  availableSkillSlugs = [],
  selectedSkillSlugs = [],
  selectionAtLimit = false,
  onSelectSuggestedSkill,
}) => {
  const availableSlugs = new Set(availableSkillSlugs);
  const selectedSlugs = new Set(selectedSkillSlugs);

  return (
    // Both hosts hand this whatever is left between their header and their
    // composer, and the two are nowhere near each other: the Excel pane sits
    // under the FactSet ribbon and its own tab strip, leaving little more
    // than this copy's own height, while the PowerPoint pane keeps most of
    // the window.
    //
    // So there is no fixed vertical inset to outgrow. `my-auto` splits
    // whatever is left over evenly, which centres the copy in a tall pane and
    // collapses to nothing in a short one - a fixed inset could only ever be
    // right for one of them, and in the short pane it pushed the copy out of
    // a container it would otherwise have fitted.
    <div className="my-auto flex flex-col items-center px-4 text-center">
      <span className="text-[18px] font-medium leading-5 text-foreground">
        Let&apos;s get started
      </span>

      <div className="mt-4 flex flex-col items-center gap-3" aria-label="Suggested skills">
        {SUGGESTED_SKILL_PATHS[host].map((path) => {
          const slug = path.slice(1);
          const selected = selectedSlugs.has(slug);
          const unavailable = !availableSlugs.has(slug);
          const disabled =
            !onSelectSuggestedSkill ||
            unavailable ||
            selected ||
            (!selected && selectionAtLimit);

          return (
            <button
              key={path}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onSelectSuggestedSkill?.(path)}
              className="inline-flex h-6 cursor-pointer items-center gap-1.5 rounded-full border border-border px-2 text-[12px] font-medium leading-[18px] text-foreground transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-50"
            >
              <ScrollText className="size-4" aria-hidden />
              {path}
            </button>
          );
        })}
      </div>

      {manageSkillsUrl && (
        <div className="mt-4 text-[12px] leading-4 text-muted-foreground">
          <a
            href={manageSkillsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-foreground underline underline-offset-2"
          >
            Manage Skills
          </a>
          <br />
          on your FactSet account
        </div>
      )}
    </div>
  );
};
